const post = {
  slug: 'youtube-topic-research',
  cluster: 'topics',
  pillar: true,
  eyebrow: 'Topics',
  title: 'Finding topics worth making a video about',
  description:
    'The difference between a subject and a topic, where good ones actually come from, and the test that kills most of your ideas before you waste a week on them.',
  keywords: [
    'youtube topic research', 'youtube video ideas', 'content ideas youtube',
    'how to find video topics', 'youtube ideation', 'video topic selection',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-topic-research.webp',
    alt: 'A list of subject headings crossed out and rewritten as specific questions.',
  },
  lede:
    'Most bad videos were doomed at the topic stage, before a word was written. The topic is the single highest-leverage decision you make, and there is a test that catches the bad ones early.',
  sections: [
    {
      heading: 'A subject is not a topic',
      body: [
        {
          type: 'para',
          text: 'A subject is a thing you could talk about. A topic is a **question with an answer someone wants**.',
        },
        {
          type: 'example',
          badLabel: 'Subject',
          goodLabel: 'Topic',
          bad: 'The Concorde.',
          good: 'Why Concorde never flew the route it was designed for.',
          note: 'The first one produces a summary. The second one has an answer at the end of it, which is what makes people stay for the answer.',
        },
        {
          type: 'para',
          text: 'The practical difference: a subject video has no natural shape, so it becomes a chronology or a list of facts. A topic video has a built-in structure — ask, complicate, resolve.',
        },
      ],
    },
    {
      heading: 'The test',
      body: [
        {
          type: 'para',
          text: 'For any idea, write down the question the video answers, in one sentence, as a stranger would ask it. Then check three things:',
        },
        {
          type: 'list',
          ordered: true,
          items: [
            '**Would anyone ask this?** If you cannot imagine someone typing it or wondering it, there is no demand behind it.',
            '**Is the answer non-obvious?** If the answer is what everyone already assumes, the video has nothing to deliver.',
            '**Can you actually answer it?** With specifics — names, figures, mechanism — rather than by restating the question at greater length.',
          ],
        },
        {
          type: 'para',
          text: 'Ideas failing (2) are the most seductive, because they feel like solid topics. "Why is the sky blue" fails it for most audiences. "Why the sky on Mars is blue only at sunset" does not.',
        },
      ],
    },
    {
      heading: 'Where topics actually come from',
      body: [
        {
          type: 'sub',
          text: 'Things that contradict an assumption',
        },
        {
          type: 'para',
          text: 'The strongest source. Anywhere the true answer differs from the intuitive one, there is a video — the gap is the tension, for free.',
        },
        {
          type: 'sub',
          text: 'Questions people ask badly',
        },
        {
          type: 'para',
          text: 'Forums, comment sections, and the "people also ask" style suggestions are full of questions asked imprecisely because the asker does not have the vocabulary. Those are underserved almost by definition, because content is written against the precise phrasing.',
        },
        {
          type: 'sub',
          text: 'The second question',
        },
        {
          type: 'para',
          text: 'Take a well-covered topic and ask what someone wonders *after* watching those videos. The first question is saturated; the follow-up usually is not.',
        },
        {
          type: 'sub',
          text: 'Specific instances of general phenomena',
        },
        {
          type: 'para',
          text: 'Not "how do bank runs work" but the specific bank, on the specific date. The general version has been made a hundred times; the instance carries the mechanism *and* has real photographs attached to it.',
        },
      ],
    },
    {
      heading: 'The "what does everyone else do" step',
      body: [
        {
          type: 'para',
          text: 'Before writing, watch or skim the top three existing videos on the topic and write down, explicitly: the stock framing, the same three facts they all repeat, the anecdote that opens half of them.',
        },
        {
          type: 'para',
          text: 'Then do not make that video. This one step separates a treatment worth watching from a competent restatement, and it takes twenty minutes. [Competitor research](/blogs/youtube-competitor-research) covers doing it properly.',
        },
        {
          type: 'callout',
          title: 'Being correct is not enough',
          text: 'True-but-generic is its own failure mode. A video can be entirely accurate, well produced, and still have no reason to exist because it is interchangeable with four others.',
        },
      ],
    },
    {
      heading: 'Build a list, not one idea',
      body: [
        {
          type: 'para',
          text: 'Research topics in batches and keep a running list. Two reasons.',
        },
        {
          type: 'para',
          text: 'First, per-video ideation is what stalls channels — the hardest part of publishing weekly is deciding what the video is, and doing that under time pressure produces the safe, generic choice every time.',
        },
        {
          type: 'para',
          text: 'Second, a list lets you pick the best of twenty rather than the first acceptable one. The quality difference between those two selection methods is large and compounds over a year.',
        },
      ],
    },
    {
      heading: 'Where to stop researching',
      body: [
        {
          type: 'para',
          text: 'When you can name two specifics a generic treatment would not contain — a named person, a dated event, an exact figure, the term practitioners actually use. That is the threshold for having something to say.',
        },
        {
          type: 'para',
          text: 'If you cannot get to two, you do not know the topic well enough to make the video yet. That is a research problem, and no amount of scripting technique fixes it.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Should I make videos on topics that are trending?',
      a: 'Sometimes, but understand the trade: trending topics have demand now and none in six months, while evergreen ones accumulate. See evergreen versus trending for how to think about the mix.',
    },
    {
      q: 'What if my topic has already been covered by a big channel?',
      a: 'That is evidence of demand, and it is only a problem if you would be making the same video. Find the question their video raised and did not answer — that one is unserved and specifically interesting to the people who watched theirs.',
    },
    {
      q: 'How many topics should I have queued?',
      a: 'Enough that you never write under ideation pressure. Twenty before starting, and topping the list up faster than you consume it, is a reasonable working rule.',
    },
  ],
  related: ['youtube-video-ideas-that-work', 'youtube-competitor-research', 'evergreen-vs-trending-youtube'],
};

export default post;
